/**
 * Sync foyers serveur ↔ local — Phase 4, incrément 4.5.
 *
 * Orchestre la réconciliation entre le store local
 * (`apps/web/src/stores/homes.ts`) et l'API
 * (`apps/web/src/api/homes.ts`). Suit la stratégie server-wins
 * simple actée par l'ADR 0009 :
 *
 *   1. Les foyers locaux pré-auth (id format `home_<…>`, non-UUID)
 *      sont migrés via `POST /homes`. Leur id local est remplacé
 *      par l'UUID retourné.
 *   2. La liste retournée par `GET /homes` est l'autorité — name +
 *      color du serveur écrasent le local. Le champ `data`
 *      (events, tasks, etc.) local est préservé : les entités sub
 *      auront leur propre sync en 4.6+.
 *   3. Les foyers locaux avec un id UUID absent du serveur sont
 *      supprimés localement (server-wins).
 *
 * Mode local-only (CLAUDE.md §2.4) préservé : si l'API ne répond
 * pas, on warn console et on laisse le state local intact.
 *
 * Le wiring « sync au boot + après login/register » vit dans
 * `main.ts` qui s'abonne à la session.
 */
import * as homesApi from '../api/homes.js';
import { ApiError } from '../api/client.js';
import {
  applySyncResult,
  getHomesSnapshot,
  isServerHomeId,
  type ServerHomePayload,
} from './homes.js';
import { syncBagForAllHomes } from './bag-sync.js';
import { syncEventsForAllHomes } from './events-sync.js';
import { syncExpensesForAllHomes } from './expenses-sync.js';
import { syncJournalForAllHomes } from './journal-sync.js';
import { syncLiaisonForAllHomes } from './liaison-sync.js';
import { syncMessagesForAllHomes } from './messages-sync.js';
import { syncPhotosForAllHomes } from './photos-sync.js';
import { syncSchoolForAllHomes } from './school-sync.js';
import { syncTasksForAllHomes } from './tasks-sync.js';
import type { Home } from '@nestor/shared-types';

/**
 * État de la dernière sync — exposé pour les tests et un futur
 * indicateur UI (« dernière synchro à 14:32 »).
 */
export interface SyncStatus {
  state: 'idle' | 'syncing' | 'ok' | 'error';
  /** Timestamp en ms de la dernière sync réussie (ou erreur). */
  lastAt: number | null;
  /** Code/message en cas d'erreur — neutre, mappé i18n côté UI. */
  errorCode: string | null;
}

let status: SyncStatus = { state: 'idle', lastAt: null, errorCode: null };
let inFlight: Promise<void> | null = null;

export function getSyncStatus(): SyncStatus {
  return status;
}

/**
 * Synchronise la liste des foyers avec le serveur. Idempotent :
 * appel multiple = un seul round-trip serveur tant que le précédent
 * n'est pas résolu.
 *
 * Stratégie (ADR 0009) :
 *
 *   1. Migrer les foyers locaux pré-auth (id non-UUID) via POST.
 *      Échec ponctuel = on conserve le home local pour retenter
 *      au prochain sync.
 *   2. Fetch GET /homes → autorité finale.
 *   3. `applySyncResult({ idMap, serverHomes })` en une transaction
 *      pour un seul render UI.
 *
 * Erreurs réseau / 401 / 503 : warn console, status passe à
 * `'error'`, state local inchangé. L'UI continue de fonctionner.
 */
export async function syncHomesWithServer(): Promise<void> {
  if (inFlight) return inFlight;
  inFlight = doSync().finally(() => {
    inFlight = null;
  });
  return inFlight;
}

async function doSync(): Promise<void> {
  status = { state: 'syncing', lastAt: status.lastAt, errorCode: null };
  const snapshot = getHomesSnapshot();

  // 1. Migration des foyers pré-auth (id non-UUID).
  const idMap = new Map<string, string>();
  const localOnly = snapshot.homes.filter((h) => !isServerHomeId(h.id));
  for (const home of localOnly) {
    try {
      const created = await homesApi.createHome(buildCreateInput(home));
      idMap.set(home.id, created.id);
    } catch (err) {
      // Échec ponctuel (network, 401…) : on log + on conserve. Si
      // c'est un 401, le fetch suivant échouera aussi et on tombera
      // dans le catch d'en-dessous — pas la peine d'abort tout de
      // suite (peut-être seulement ce POST particulier qui a planté).
      console.warn('[homes-sync] migration POST échouée', {
        homeId: home.id,
        error: errorCode(err),
      });
    }
  }

  // 2. Fetch + 3. apply.
  try {
    const serverHomes = await homesApi.listHomes();
    const serverPayload: ServerHomePayload[] = serverHomes.map((h) => ({
      id: h.id,
      name: h.name,
      mode: h.mode,
      color: h.color,
    }));
    applySyncResult({ idMap, serverHomes: serverPayload });
    status = { state: 'ok', lastAt: Date.now(), errorCode: null };
    // Phase 4.6+ : chaîne la sync des sub-entités. Les homes ont
    // maintenant leurs UUIDs serveur, donc les 9 sub-syncs peuvent
    // itérer dessus sans risque de taper l'API avec un id local.
    // Fire-and-forget en parallèle — sub-entités indépendantes
    // (pas d'ordre requis). 4 entités classic (events/tasks/
    // messages/photos) + 5 entités coparenting (journal, liaison,
    // expenses, bag, school). **Sous-phase 4.10 complète** — toutes
    // les sub-entités coparenting du proto sont synchronisées.
    // Chaque sub-sync coparenting filtre interne par mode.
    void syncEventsForAllHomes();
    void syncTasksForAllHomes();
    void syncMessagesForAllHomes();
    void syncPhotosForAllHomes();
    void syncJournalForAllHomes();
    void syncLiaisonForAllHomes();
    void syncExpensesForAllHomes();
    void syncBagForAllHomes();
    void syncSchoolForAllHomes();
  } catch (err) {
    // Pas de liste serveur — on applique quand même le remap des
    // homes migrés (pour ne pas garder un id local mort) puis on
    // signale l'erreur.
    if (idMap.size > 0) {
      applySyncResult({ idMap });
    }
    const code = errorCode(err);
    console.warn('[homes-sync] fetch /homes échoué', { error: code });
    status = { state: 'error', lastAt: Date.now(), errorCode: code };
  }
}

function buildCreateInput(home: Home): homesApi.CreateHomeInput {
  return {
    name: home.name,
    mode: home.mode,
    ...(home.color ? { color: home.color } : {}),
  };
}

function errorCode(err: unknown): string {
  if (err instanceof ApiError) {
    return err.code ?? `http_${err.status.toString()}`;
  }
  if (err instanceof Error) return err.name;
  return 'unknown';
}

/**
 * @internal pour tests — reset l'état module-level (status + inflight)
 * pour permettre l'isolation entre cas.
 */
export function _resetSyncForTests(): void {
  status = { state: 'idle', lastAt: null, errorCode: null };
  inFlight = null;
}
